import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import PMSwitch from "../../../utils/PMSwitch";

/** True when an earlier version was approved (now approved or obsolete). */
export const hasEarlierApprovedVersion = (versions = [], versionNumber) =>
  versions.some(
    (version) =>
      Number(version.version) < Number(versionNumber) && ["approved", "obsolete"].includes(version.status)
  );

/**
 * Confirm dialog for approving a revision of an HMS document: choose whether readers must
 * confirm «lest og forstått» again (major change) or keep earlier confirmations (minor fix).
 */
export const ApproveRevisionDialog = ({ open, onClose, onConfirm, versionNumber, submitting = false, error }) => {
  const { t } = useTranslation();
  const [requiresReack, setRequiresReack] = useState(true);

  useEffect(() => {
    if (open) setRequiresReack(true);
  }, [open]);

  return (
    <PMDialog
      open={open}
      onClose={submitting ? () => {} : onClose}
      size="2xl"
      eyebrow={t("hseCompliance.approve.eyebrow")}
      title={t("hseCompliance.approve.title", { version: versionNumber })}
      subtitle={t("hseCompliance.approve.subtitle")}
    >
      <div className="flex flex-col">
        <div className="px-4 py-4 sm:px-6">
          <RequiresReacknowledgementSwitch checked={requiresReack} onChange={setRequiresReack} />
        </div>
        <div className="pm-form-footer">
          {error ? (
            <div className="pm-form-footer__summary" role="alert">
              <span className="pm-form-footer__summary-icon" aria-hidden="true">!</span>
              <span>{error}</span>
            </div>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className="pm-btn pm-btn--secondary" onClick={onClose} disabled={submitting}>
              {t("Avbryt")}
            </button>
            <button
              type="button"
              className="pm-btn pm-btn--primary"
              disabled={submitting}
              aria-busy={submitting || undefined}
              onClick={() => onConfirm?.(requiresReack)}
            >
              {submitting ? <span className="pm-btn__spinner" aria-hidden="true" /> : null}
              {t("Godkjenn versjon")}
            </button>
          </div>
        </div>
      </div>
    </PMDialog>
  );
};

/** The switch row on its own (also used on the approval page). */
export const RequiresReacknowledgementSwitch = ({ checked, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <PMSwitch
      variant="row"
      name="requires_reacknowledgement"
      label={t("hseCompliance.approve.reack_label")}
      description={checked ? t("hseCompliance.approve.reack_on") : t("hseCompliance.approve.reack_off")}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.checked)}
    />
  );
};

export default ApproveRevisionDialog;
